// @flow

/**
 * Prompt Chat Controller Hook
 * Generic controller for send, cancel, reset operations
 * Uses the streaming adapter pattern instead of hardcoded service
 */

import { useCallback, useRef, useEffect } from 'react';
import { usePromptChatState, usePromptChatDispatch, useStreamingAdapter } from '../context';
import { DEFAULT_MAX_PROMPT_CHARS } from '../constants';

export function usePromptChatController() {
  const state = usePromptChatState();
  const dispatch = usePromptChatDispatch();
  const adapter = useStreamingAdapter();

  const {
    isStreaming,
    promptLocked,
    sessionId,
    maxPromptChars = DEFAULT_MAX_PROMPT_CHARS,
    initialAgentText: stateInitialAgentText = '',
  } = state;

  /**
   * Send a prompt and initiate streaming
   */
  const send = useCallback(async (prompt: string) => {
    const trimmed = prompt.trim();
    
    // Validation
    if (!trimmed) return;
    if (isStreaming) return;
    if (promptLocked) return;
    if (trimmed.length > maxPromptChars) return;
    if (!sessionId) return;

    // Add user message
    const userMsg = {
      id: `u-${Date.now()}`,
      role: 'user',
      text: trimmed,
    };
    dispatch({ type: 'ADD_USER_MESSAGE', message: userMsg });

    // Create agent placeholder with initial Thinking event
    const agentId = `a-stream-${Date.now()}`;
    const thinkingEvent = {
      id: `ev-${Date.now()}`,
      text: 'Thinking',
      withDots: true,
      startAt: Date.now(),
    };
    dispatch({
      type: 'ADD_AGENT_PLACEHOLDER',
      message: {
        id: agentId,
        role: 'agent',
        text: '',
        events: [thinkingEvent],
      },
    });

    // Start streaming
    dispatch({ type: 'START_STREAM', agentMessageId: agentId });

    try {
      // Call the streaming adapter to handle backend communication
      await adapter.send(trimmed, agentId);
    } catch (err) {
      // Stream initialization error
      const errorMessage = (err && err.message) || 'Error starting stream';
      dispatch({
        type: 'SET_ERROR',
        error: { type: 'generic', message: errorMessage },
      });
      dispatch({ type: 'END_STREAM' });
    }
  }, [dispatch, adapter, isStreaming, sessionId, maxPromptChars]);

  /**
   * Cancel the current stream
   */
  const cancel = useCallback(() => {
    adapter.cancel();
    
    // Finalize last event and end stream
    if (isStreaming && state.activeStreamMessageId) {
      dispatch({
        type: 'FINALIZE_LAST_EVENT',
        agentMessageId: state.activeStreamMessageId,
      });
      dispatch({ type: 'END_STREAM' });
    }
  }, [adapter, dispatch, isStreaming, state.activeStreamMessageId]);

  /**
   * Reset the chat (new session)
   */
  const reset = useCallback((initialAgentText?: string) => {
    // Cancel any active stream via adapter
    adapter.reset();

    // Generate new session ID
    const newSessionId =
      (window.crypto &&
        window.crypto.randomUUID &&
        window.crypto.randomUUID()) ||
      Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);

    // Dispatch reset (use provided text or fallback to state's initialAgentText)
    dispatch({
      type: 'RESET_CHAT',
      initialAgentText: initialAgentText !== undefined ? initialAgentText : stateInitialAgentText,
      newSessionId,
    });
  }, [dispatch, adapter, stateInitialAgentText]);

  /**
   * Hydrate chat with historical messages
   * Used for loading conversation history
   */
  const hydrate = useCallback((messages: Array<Object>, sessionId: string) => {
    // Cancel any active stream
    adapter.reset();
    
    // Dispatch hydrate action
    dispatch({
      type: 'HYDRATE_MESSAGES',
      messages,
      sessionId,
    });
  }, [dispatch, adapter]);

  return {
    send,
    cancel,
    reset,
    hydrate,
    isPromptLocked: promptLocked,
  };
}
